import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import SupportMailLink from "@/components/SupportMailLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Circle, PencilSimple, WarningCircle, Compass, CaretDown, CaretUp, ArrowsDownUp, X } from "@phosphor-icons/react";
import { useNavigate, useSearchParams } from "react-router-dom";

import ChecklistSection from "./organizzativa/ChecklistSection";
import AuthPromptModal from "@/components/AuthPromptModal";
import AnonBanner from "@/components/AnonBanner";
import { useAnonPrompt } from "@/lib/anonPrompt";
import { trackEvent } from "@/lib/analytics";

const DEFAULT_PROFILE = {
  origin_country: "IT",
  home_school: "",
  study_track: "",
  study_track_other: "",
  destination_country: "",
  agency_id: "",
  agency_other: "",
  school_year_abroad: "2026/2027",
  duration: "",
  italian_school_year: "",
  diploma_goal: "",
};

const SCHOOL_YEARS = ["2026/2027", "2027/2028", "2028/2029", "2029/2030"];

function DepartureProfileForm({ initial, agencyGroups, studyTracks, countries, onSaved, onCancel, t, isAnonymous, onAnonymousMutation }) {
  const [form, setForm] = useState({ ...DEFAULT_PROFILE, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [comboWarn, setComboWarn] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const isSupportedCombo = form.origin_country === "IT" && form.destination_country === "US";

  // Trigger the warning modal as soon as the user picks an unsupported combo
  // (only when both fields are filled and the combo is not IT→US).
  useEffect(() => {
    if (form.origin_country && form.destination_country && !isSupportedCombo) {
      setComboWarn(true);
    }
  }, [form.origin_country, form.destination_country, isSupportedCombo]);

  // Fase 3: partial saves are always allowed. The Save button is disabled
  // only while an inflight request is pending — every other combination of
  // fields (including all-blank) can be persisted so the user is never
  // blocked from closing the panel with whatever they've entered.
  const submit = async () => {
    if (isAnonymous) onAnonymousMutation?.(t.common.authReasonSaveProfile);
    setSaving(true);
    try {
      const res = await api.post("/departure-profile", form);
      onSaved(res.data);
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="departure-profile-form" className="bg-surface-card rounded-3xl border border-border p-6 sm:p-8 space-y-5">
      <div className="flex items-center gap-2">
        <Compass size={22} weight="duotone" className="text-terracotta" />
        <h2 className="font-display font-extrabold text-2xl text-ink tracking-tight">
          {t.modes.organizzativa.profileTitle}
        </h2>
      </div>
      <p className="text-sm text-ink-soft leading-relaxed">
        {t.modes.organizzativa.profileSubtitle}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileOrigin}
          </Label>
          <Select value={form.origin_country} onValueChange={(v) => set("origin_country", v)}>
            <SelectTrigger data-testid="profile-origin" className="rounded-xl h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {countries.origin.map(c => (<SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileDestination}
          </Label>
          <Select value={form.destination_country} onValueChange={(v) => set("destination_country", v)}>
            <SelectTrigger data-testid="profile-destination" className="rounded-xl h-11">
              <SelectValue placeholder={t.modes.organizzativa.profileDestinationPh} />
            </SelectTrigger>
            <SelectContent>
              {countries.destination.map(c => (<SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileHomeSchool}
          </Label>
          <Input
            data-testid="profile-home-school"
            value={form.home_school}
            onChange={(e) => set("home_school", e.target.value)}
            placeholder={t.modes.organizzativa.profileHomeSchoolPh}
            className="rounded-xl h-11 font-sans"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileStudyTrack}
          </Label>
          <Select value={form.study_track} onValueChange={(v) => set("study_track", v)}>
            <SelectTrigger data-testid="profile-study-track" className="rounded-xl h-11">
              <SelectValue placeholder={t.modes.organizzativa.profileStudyTrackPh} />
            </SelectTrigger>
            <SelectContent>
              {studyTracks.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
          {form.study_track === "other" && (
            <Input
              data-testid="profile-study-track-other"
              value={form.study_track_other}
              onChange={(e) => set("study_track_other", e.target.value)}
              placeholder={t.modes.organizzativa.profileStudyTrackOther}
              className="rounded-xl h-11 font-sans mt-2"
            />
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileAgency}
          </Label>
          <div className="flex items-center gap-2">
            <Select value={form.agency_id} onValueChange={(v) => set("agency_id", v)}>
              <SelectTrigger data-testid="profile-agency" className="rounded-xl h-11 flex-1">
                <SelectValue placeholder={t.modes.organizzativa.profileAgencyPh} />
              </SelectTrigger>
              <SelectContent>
                {agencyGroups.map((g, gi) => (
                  <SelectGroup key={gi}>
                    <SelectLabel>{g.label}</SelectLabel>
                    {g.items.map(a => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {form.agency_id && (
              <button
                type="button"
                data-testid="profile-agency-clear"
                aria-label={t.modes.organizzativa.profileClearField}
                onClick={() => { set("agency_id", ""); set("agency_other", ""); }}
                className="h-11 w-11 shrink-0 rounded-xl border border-border bg-surface text-ink-soft hover:text-ink hover:border-terracotta/50 transition-colors flex items-center justify-center"
              >
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
          {form.agency_id === "other" && (
            <Input
              data-testid="profile-agency-other"
              value={form.agency_other}
              onChange={(e) => set("agency_other", e.target.value)}
              placeholder={t.modes.organizzativa.profileAgencyOther}
              className="rounded-xl h-11 font-sans mt-2"
            />
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileYearAbroad}
          </Label>
          <Select value={form.school_year_abroad} onValueChange={(v) => set("school_year_abroad", v)}>
            <SelectTrigger data-testid="profile-year-abroad" className="rounded-xl h-11">
              <SelectValue placeholder={t.modes.organizzativa.profileYearAbroadPh} />
            </SelectTrigger>
            <SelectContent>
              {SCHOOL_YEARS.map(y => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileDuration}
          </Label>
          <div className="flex items-center gap-2">
            <Select value={form.duration} onValueChange={(v) => set("duration", v)}>
              <SelectTrigger data-testid="profile-duration" className="rounded-xl h-11 flex-1">
                <SelectValue placeholder={t.modes.organizzativa.profileDurationPh || "—"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trimester">{t.modes.organizzativa.durationTrimester}</SelectItem>
                <SelectItem value="semester">{t.modes.organizzativa.durationSemester}</SelectItem>
                <SelectItem value="full_year">{t.modes.organizzativa.durationFullYear}</SelectItem>
              </SelectContent>
            </Select>
            {form.duration && (
              <button
                type="button"
                data-testid="profile-duration-clear"
                aria-label={t.modes.organizzativa.profileClearField}
                onClick={() => set("duration", "")}
                className="h-11 w-11 shrink-0 rounded-xl border border-border bg-surface text-ink-soft hover:text-ink hover:border-terracotta/50 transition-colors flex items-center justify-center"
              >
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileItalianYear}
          </Label>
          <Select value={form.italian_school_year} onValueChange={(v) => set("italian_school_year", v)}>
            <SelectTrigger data-testid="profile-italian-year" className="rounded-xl h-11">
              <SelectValue placeholder={t.modes.organizzativa.profileItalianYearPh} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="third">{t.modes.organizzativa.italianYearThird}</SelectItem>
              <SelectItem value="fourth">{t.modes.organizzativa.italianYearFourth}</SelectItem>
              <SelectItem value="fifth">{t.modes.organizzativa.italianYearFifth}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-ink-soft font-sans font-semibold">
            {t.modes.organizzativa.profileDiplomaGoal}
          </Label>
          <div className="flex flex-col sm:flex-row gap-2">
            {[
              { v: "us_diploma", label: t.modes.organizzativa.profileDiplomaUS },
              { v: "italian_diploma", label: t.modes.organizzativa.profileDiplomaIT },
            ].map(opt => (
              <button
                key={opt.v}
                type="button"
                data-testid={`profile-diploma-${opt.v}`}
                onClick={() => set("diploma_goal", form.diploma_goal === opt.v ? "" : opt.v)}
                className={`flex-1 text-left rounded-xl border-2 px-3 py-2 text-sm font-sans transition-colors ${
                  form.diploma_goal === opt.v
                    ? "border-terracotta bg-terracotta/5 text-ink"
                    : "border-border bg-surface hover:border-terracotta/40 text-ink"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="pt-2 flex flex-wrap items-center gap-2">
        <Button
          data-testid="profile-save"
          onClick={submit}
          disabled={saving}
          className="rounded-full h-12 px-6 bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t.modes.organizzativa.profileSavePartial}
        </Button>
        {onCancel && (
          <Button
            data-testid="profile-close"
            variant="ghost"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full h-12 px-5 text-ink-soft hover:text-ink hover:bg-transparent font-sans"
          >
            {t.modes.organizzativa.profilePanelClose}
          </Button>
        )}
      </div>

      {/* Unsupported combo modal — activates on selection, not only on save.
          Closing the popup automatically clears the unsupported destination
          so the field never stays stuck on a non-supported country. */}
      <Dialog
        open={comboWarn}
        onOpenChange={(open) => {
          setComboWarn(open);
          if (!open && !isSupportedCombo) {
            // Reset the offending field so the visible selection matches the
            // checklist actually served (default IT→US when empty).
            set("destination_country", "");
          }
        }}
      >
        <DialogContent data-testid="combo-warn-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-xl text-ink">
              {t.modes.organizzativa.unsupportedComboTitle}
            </DialogTitle>
            <DialogDescription className="text-ink-soft leading-relaxed">
              {t.modes.organizzativa.unsupportedComboBody}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="combo-warn-close"
              onClick={() => {
                setComboWarn(false);
                if (!isSupportedCombo) set("destination_country", "");
              }}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold"
            >
              {t.modes.organizzativa.unsupportedComboClose}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default function Organizzativa() {
  const { t, lang, user, loadingUser, phaseState, refreshPhaseState } = useApp();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [countries, setCountries] = useState({ origin: [], destination: [], supported_combos: [] });
  const [agencyGroups, setAgencyGroups] = useState([]);
  const [studyTracks, setStudyTracks] = useState([]);
  const [checklist, setChecklist] = useState({ template_id: "", items: [], unavailable: false });
  const [vaccinations, setVaccinations] = useState({ template_id: "", items: [], intro_note: "" });
  const [editingProfile, setEditingProfile] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  // Status filter (new): "all" | "todo" | "done". Persisted for the current
  // session only (sessionStorage), never saved server-side.
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return sessionStorage.getItem("organizza:statusFilter") || "all"; }
    catch { return "all"; }
  });
  // IDs the user just toggled during the current status-filter session.
  // Keeps them visible until the filter changes (so the task doesn't vanish
  // the instant the user ticks the box).
  const [stuckIds, setStuckIds] = useState(() => new Set());
  const changeStatusFilter = (next) => {
    setStatusFilter(next);
    setStuckIds(new Set()); // reset pins on filter change
    try { sessionStorage.setItem("organizza:statusFilter", next); } catch {}
  };
  const [checklistSort, setChecklistSort] = useState("asc"); // "asc" = nearest first, "desc" = furthest first
  const [expandedItems, setExpandedItems] = useState(() => new Set());
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const prevProgressRef = React.useRef(null);

  // Fire Plausible custom event on mount so the conversion funnel can track
  // organizing-phase engagement consistently, same pattern as Landing and
  // ABordo. Goal must be configured on the Plausible dashboard side.
  useEffect(() => {
    trackEvent("Organizza Viewed");
  }, []);

  // Watch for the transition to 100% plan completion: show the modal only when
  // crossing from <1 to 1, and only if the user has never acknowledged it before.
  useEffect(() => {
    const cur = phaseState?.plan?.progress;
    const prev = prevProgressRef.current;
    if (
      cur != null &&
      cur >= 1 &&
      (prev == null || prev < 1) &&
      !phaseState?.plan_completion_acknowledged
    ) {
      setShowCompletionModal(true);
    }
    prevProgressRef.current = cur;
  }, [phaseState]);

  // Open the departure-profile edit form immediately when the caller passes
  // ?edit=profile (e.g. the "Modifica profilo" button in the Bacheca). The
  // panel is opt-in — clicking the link opens it even for users who haven't
  // saved a profile yet.
  useEffect(() => {
    if (searchParams.get("edit") !== "profile") return;
    if (!profileLoaded) return;
    openProfilePanel();
    requestAnimationFrame(() => {
      document
        .querySelector('[data-testid="departure-profile-form"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  }, [searchParams, profileLoaded, setSearchParams]);

  const acknowledgePlanCompletion = async () => {
    try { await api.post("/phase-progress/ack-plan-completed"); } catch { /* non-blocking */ }
    await refreshPhaseState();
  };

  const toggleExpanded = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Compute an "effective" deadline for sorting: use user-set deadline if present,
  // otherwise fall back to today + days_before_departure as an implicit deadline
  // so the order still reflects urgency for items the user hasn't scheduled yet.
  const sortedChecklistItems = React.useMemo(() => {
    const nowMs = Date.now();
    const withKeys = (checklist.items || []).map(it => {
      let effective;
      if (it.deadline) {
        const parsed = Date.parse(it.deadline);
        effective = Number.isNaN(parsed) ? null : parsed;
      }
      if (effective == null && typeof it.days_before_departure === "number") {
        effective = nowMs + it.days_before_departure * 86_400_000;
      }
      return { it, effective };
    });
    withKeys.sort((a, b) => {
      // items with no effective key are pushed to the end
      if (a.effective == null && b.effective == null) return 0;
      if (a.effective == null) return 1;
      if (b.effective == null) return -1;
      return checklistSort === "asc" ? a.effective - b.effective : b.effective - a.effective;
    });
    return withKeys.map(x => x.it);
  }, [checklist.items, checklistSort]);

  // Urgency level based on days until effective deadline (deadline OR
  // today+days_before_departure). Returns one of: "urgent" (<7d),
  // "soon" (7-30d), "ok" (>30d), or null if there's no time reference.
  const urgencyFor = React.useCallback((item) => {
    const nowMs = Date.now();
    let effectiveMs;
    if (item.deadline) {
      const parsed = Date.parse(item.deadline);
      if (!Number.isNaN(parsed)) effectiveMs = parsed;
    }
    if (effectiveMs == null && typeof item.days_before_departure === "number") {
      effectiveMs = nowMs + item.days_before_departure * 86_400_000;
    }
    if (effectiveMs == null) return null;
    const daysLeft = Math.ceil((effectiveMs - nowMs) / 86_400_000);
    if (daysLeft < 7) return "urgent";
    if (daysLeft <= 30) return "soon";
    return "ok";
  }, []);

  useEffect(() => {
    if (loadingUser) return;
    (async () => {
      const requests = [
        api.get(`/content/countries?lang=${lang}`),
        api.get(`/content/agency-options?lang=${lang}`),
        api.get(`/content/study-tracks?lang=${lang}`),
        // /departure-profile now accepts anonymous requests too (Fase 2):
        // returns the anon draft if present, or null.
        api.get("/departure-profile").catch(() => ({ data: null })),
      ];
      const results = await Promise.all(requests);
      setCountries(results[0].data);
      setAgencyGroups(results[1].data);
      setStudyTracks(results[2].data);
      setProfile(results[3].data);
      setProfileLoaded(true);
    })();
  }, [lang, user, loadingUser, navigate]);

  const supportedCombo = (p) => !!p && !!(countries.supported_combos || []).find(
    c => c.origin === p.origin_country && c.destination === p.destination_country
  );

  const loadChecklist = async () => {
    // /checklists/my now works for anonymous users too (Fase 2): the backend
    // resolves the actor scope and returns saved drafts from the anon_*
    // shadow collections. Falls back gracefully if the anon has no profile.
    const [docsRes, vaxRes] = await Promise.all([
      api.get(`/checklists/my?lang=${lang}`),
      api.get(`/vaccinations/my?lang=${lang}`).catch(() => ({ data: { items: [], intro_note: "" } })),
    ]);
    setChecklist(docsRes.data);
    setVaccinations(vaxRes.data);
  };

  useEffect(() => {
    if (!profileLoaded) return;
    // Fase 3: checklist is ALWAYS loaded — the tasks must be visible on
    // page entry regardless of whether a profile is set. The backend falls
    // back to the IT→US default when no profile (or an unsupported combo)
    // is saved.
    loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, editingProfile, lang, user, profileLoaded]);

  // ---------- One-shot auth-prompt nudge for anonymous users ----------
  // Mutations are actually performed (saved to anon_* shadow collections and
  // migrated on signup). The modal is a friendly reminder shown ONCE per
  // device so anon users know their work is safe but can be moved to a real
  // account for cross-device access.
  const { open: authPromptOpen, setOpen: setAuthPromptOpen, reason: authPromptReason, nudge: openAuthPrompt, show: forceAuthPrompt } = useAnonPrompt();
  const isAnonymous = !user;

  // The departure-profile form is fully auth-gated: opening it always
  // triggers the AuthPromptModal for anonymous users (bypassing the one-shot
  // flag). Registered users get the form as normal. When `field` is passed
  // (e.g. from a Linked task's "Modifica nel profilo" click), the form
  // scrolls the specific field into view after render.
  const openProfilePanel = (field) => {
    if (isAnonymous) {
      forceAuthPrompt(t.common.authReasonSaveProfile);
      return;
    }
    setEditingProfile(true);
    // Map linked_profile_field values to their concrete data-testid inside
    // the DepartureProfileForm. Unmapped fields fall back to the form root.
    const FIELD_TESTID = {
      agency_id: "profile-agency",
      duration: "profile-duration",
      school_year_abroad: "profile-year-abroad",
      italian_school_year: "profile-italian-year",
      diploma_goal: "profile-diploma-us_diploma",
      home_school: "profile-home-school",
      study_track: "profile-study-track",
      destination_country: "profile-destination",
      origin_country: "profile-origin",
    };
    // Two rAF hops so React can commit the form + Radix Select triggers.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const testid = field && FIELD_TESTID[field];
      const el = (testid && document.querySelector(`[data-testid="${testid}"]`))
        || document.querySelector('[data-testid="departure-profile-form"]');
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }));
  };

  // Generic toggle used by both docs and vaccinations checklists.
  // For anonymous users we BLOCK the mutation and show the AuthPromptModal
  // every time — behavior is fully consistent across all task types
  // (checkbox, deadline, selection, user_input, subtask, custom item).
  const toggleItem = async (item) => {
    if (isAnonymous) {
      forceAuthPrompt(t.common.authReasonToggleTask);
      return;
    }
    const nextChecked = !item.checked;
    // Pin the item so it stays visible when the active status filter would
    // normally hide it (e.g. ticking a task while on "Da fare"). The pin is
    // cleared when the user changes the filter or reloads the page.
    if (statusFilter !== "all") {
      setStuckIds(prev => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
    }
    // Optimistic update on the correct list
    if (item._section === "vax") {
      setVaccinations(v => ({ ...v, items: v.items.map(i => i.id === item.id ? { ...i, checked: nextChecked } : i) }));
    } else {
      setChecklist(cl => ({ ...cl, items: cl.items.map(i => i.id === item.id ? { ...i, checked: nextChecked } : i) }));
    }
    await api.post("/checklists/toggle", {
      template_id: item._templateId,
      item_id: item.id,
      checked: nextChecked,
      deadline: item.deadline || null,
    });
    refreshPhaseState();
  };

  const setDeadline = async (item, value) => {
    if (isAnonymous) {
      forceAuthPrompt(t.common.authReasonSetDeadline);
      return;
    }
    if (item._section === "vax") {
      setVaccinations(v => ({ ...v, items: v.items.map(i => i.id === item.id ? { ...i, deadline: value } : i) }));
    } else {
      setChecklist(cl => ({ ...cl, items: cl.items.map(i => i.id === item.id ? { ...i, deadline: value } : i) }));
    }
    await api.post("/checklists/toggle", {
      template_id: item._templateId,
      item_id: item.id,
      checked: item.checked,
      deadline: value || null,
    });
  };

  if (loadingUser || !profileLoaded) return <div className="p-8 text-ink-soft">{t.common.loading}</div>;

  const hasProfile = !!profile;
  const comboUnsupported = !!checklist.combo_unsupported;
  // Build a compact human-readable summary of whatever profile fields the
  // user has already filled in. Empty parts are simply omitted, so a partial
  // profile still renders a meaningful chip.
  const profileSummaryParts = [];
  if (hasProfile) {
    const origin = countries.origin.find(c => c.code === profile.origin_country);
    const dest = countries.destination.find(c => c.code === profile.destination_country);
    if (origin && dest) profileSummaryParts.push(`${origin.name} → ${dest.name}`);
    else if (dest) profileSummaryParts.push(dest.name);
    if (profile.school_year_abroad) profileSummaryParts.push(profile.school_year_abroad);
    if (profile.duration) profileSummaryParts.push(t.modes.organizzativa[
      profile.duration === "full_year" ? "durationFullYear"
      : profile.duration === "semester" ? "durationSemester"
      : "durationTrimester"
    ]);
    if (profile.diploma_goal) profileSummaryParts.push(
      profile.diploma_goal === "us_diploma"
        ? t.modes.organizzativa.profileDiplomaUS
        : t.modes.organizzativa.profileDiplomaIT
    );
  }
  // Only treat the profile as "meaningfully filled" when the user has picked
  // at least one field beyond the two DEFAULT_PROFILE convenience values
  // (origin_country='IT' + school_year_abroad='2026/2027') that the form
  // pre-selects for UX. This keeps the CTA visible after an "empty" save.
  const profileHasContent = hasProfile && (
    !!profile.home_school ||
    !!profile.study_track ||
    !!profile.agency_id ||
    !!profile.destination_country ||
    !!profile.duration ||
    !!profile.italian_school_year ||
    !!profile.diploma_goal
  );

  return (
    <div className="max-w-3xl mx-auto px-5 py-8 space-y-8">
      {isAnonymous && (
        <AnonBanner
          storageKey="exchangeplan.anon-banner.organizza.dismissed"
          testid="anon-banner-organizza"
        />
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
            {t.phase.plan}
          </p>
          <h1 data-testid="organizza-title" className="font-display font-black text-3xl sm:text-4xl text-ink mt-1 tracking-tight">
            {t.modes.organizzativa.title}
          </h1>
          <p className="text-ink-soft mt-2 leading-relaxed max-w-xl">
            {t.modes.organizzativa.subtitle}
          </p>
        </div>
        <MascotPlaceholder emotion="happy" src="/assets/boardy-energico.png" />
      </div>

      {/* Profile edit form — only rendered inline when the user explicitly
          opened the panel (via the smaller CTA now below the task list, or
          via ?edit=profile). When not editing, the personalize-profile
          affordance lives after the checklist. */}
      {editingProfile && (
        <DepartureProfileForm
          initial={profile}
          agencyGroups={agencyGroups}
          studyTracks={studyTracks}
          countries={countries}
          t={t}
          isAnonymous={isAnonymous}
          onAnonymousMutation={openAuthPrompt}
          onCancel={() => setEditingProfile(false)}
          onSaved={() => {
            setEditingProfile(false);
            // Re-fetch profile from server for the canonical stored version
            api.get("/departure-profile").then(r => setProfile(r.data));
            // Reload checklist so linked-profile items refresh derivation
            loadChecklist();
          }}
        />
      )}

      {/* Soft "presto disponibile" hint when the user picked an unsupported
          combo — the IT→US checklist still renders below so tasks stay
          browseable. */}
      {comboUnsupported && !editingProfile && (
        <section data-testid="unsupported-combo" className="bg-sand/25 border border-sand rounded-3xl p-5 space-y-2">
          <div className="flex items-center gap-2">
            <WarningCircle size={22} weight="duotone" className="text-terracotta" />
            <h2 className="font-display font-extrabold text-lg text-ink tracking-tight">
              {t.modes.organizzativa.unsupportedTitle}
            </h2>
          </div>
          <p className="text-ink-soft text-sm leading-relaxed">
            {t.modes.organizzativa.unsupportedBody}
          </p>
        </section>
      )}

      {/* Checklist — ALWAYS visible so tasks show immediately on page entry.
          Shortened disclaimer + progress bar now live right above the list;
          the previous standalone "Organizza la partenza" H2 was removed to
          cut repetition with the preheader label at the top of the page. */}
      <section data-testid="organize-departure">
        <p
          data-testid="checklist-deadline-disclaimer"
          className="text-xs sm:text-sm text-ink-soft/80 mb-5 leading-relaxed max-w-2xl font-sans"
        >
          {t.modes.organizzativa.organizzaDisclaimer}
          {" "}
          <SupportMailLink testid="checklist-support-link" subject="Segnalazione — Organizza la partenza" />
        </p>

        {/* Category filter pills — horizontally scrollable on narrow viewports.
            Pills whose category has zero tasks under the active status
            filter are hidden entirely (e.g. "Fatte" with no completed
            items in "Agenzia" ⇒ "Agenzia" pill disappears). "Tutte" is
            always visible so the user can go back to the full category set. */}
        {(() => {
          const allItems = checklist.items || [];
          const matchStatus = (i) =>
            statusFilter === "all" ? true :
            statusFilter === "todo" ? !i.checked :
            !!i.checked;
          const countForCategory = (catId) => allItems
            .filter(i => catId === "all" || (i.category || "documents") === catId)
            .filter(matchStatus).length;
          const CATEGORIES = [
            { id: "all",       label: t.modes.organizzativa.filterAll },
            { id: "decision",  label: t.modes.organizzativa.catDecision },
            { id: "school_it", label: t.modes.organizzativa.catSchoolIt },
            { id: "agency",    label: t.modes.organizzativa.catAgency },
            { id: "visa",      label: t.modes.organizzativa.catVisa },
            { id: "school_us", label: t.modes.organizzativa.catSchoolUs },
          ];
          return (
            <div
              className="flex gap-2 mb-4 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              data-testid="checklist-filter-pills"
            >
              {CATEGORIES
                .filter(p => p.id === "all" || countForCategory(p.id) > 0)
                .map(p => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`checklist-filter-${p.id}`}
                    onClick={() => setCategoryFilter(p.id)}
                    className={`px-3.5 h-8 rounded-full text-xs font-sans font-semibold border transition-colors shrink-0 whitespace-nowrap ${
                      categoryFilter === p.id
                        ? "bg-terracotta text-white border-terracotta"
                        : "bg-surface-card text-ink-soft border-border hover:border-terracotta/50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
            </div>
          );
        })()}

        {/* Status filter — three-state pill selector: Tutte / Da fare / Fatte.
            Shows counts next to each label (always from the UNFILTERED list,
            so the user sees the big picture even when a category is active).
            Persisted in sessionStorage. Session-only, no DB write. */}
        {(() => {
          const allItems = checklist.items || [];
          const totalAll = allItems.length;
          const doneAll  = allItems.filter(i => i.checked).length;
          const todoAll  = totalAll - doneAll;
          const STATES = [
            { id: "all",  label: t.modes.organizzativa.statusAll,  n: totalAll },
            { id: "todo", label: t.modes.organizzativa.statusTodo, n: todoAll },
            { id: "done", label: t.modes.organizzativa.statusDone, n: doneAll },
          ];
          return (
            <div
              role="group"
              aria-label={t.modes.organizzativa.statusFilterAria}
              data-testid="checklist-status-filter"
              className="flex items-center gap-1 mb-6 rounded-full border border-border bg-surface-card p-1 w-full overflow-hidden"
            >
              {STATES.map(s => {
                const active = statusFilter === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`checklist-status-${s.id}`}
                    onClick={() => changeStatusFilter(s.id)}
                    aria-pressed={active}
                    className={`flex-1 min-w-0 h-9 px-2 sm:px-3 rounded-full text-xs sm:text-sm font-sans font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                      active
                        ? "bg-terracotta text-white"
                        : "text-ink-soft hover:text-ink"
                    }`}
                  >
                    <span className="truncate">{s.label}</span>
                    <span
                      data-testid={`checklist-status-${s.id}-count`}
                      className={`text-[11px] font-bold tabular-nums shrink-0 ${
                        active ? "text-white/90" : "text-ink-soft/70"
                      }`}
                    >
                      {s.n}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {/* Progress bar — counts ALL tasks regardless of the active filters. */}
        {(() => {
          const totalAll  = (checklist.items || []).length;
          const doneAll   = (checklist.items || []).filter(i => i.checked).length;
          const pct = totalAll > 0 ? Math.round((doneAll / totalAll) * 100) : 0;
          const label = doneAll > 0
            ? t.modes.organizzativa.progressCounter(doneAll, totalAll)
            : t.modes.organizzativa.progressTotal(totalAll);
          return (
            <div data-testid="checklist-progress" className="mb-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div
                  aria-hidden="true"
                  className="h-1.5 flex-1 bg-ink/10 rounded-full overflow-hidden"
                >
                  <div
                    data-testid="checklist-progress-fill"
                    style={{ width: `${pct}%` }}
                    className="h-full bg-terracotta transition-[width] duration-500 ease-out"
                  />
                </div>
                <span
                  data-testid="checklist-progress-label"
                  className="text-xs sm:text-sm font-sans font-semibold text-ink-soft shrink-0 tabular-nums"
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })()}

        {/* Apply both the category pill and the status pill, PLUS pin the
            IDs the user toggled since the last filter change. The pins keep
            just-toggled items visible so the UI doesn't feel jumpy. */}
        {(() => {
          const matchStatus = (i) =>
            statusFilter === "all" ? true :
            statusFilter === "todo" ? !i.checked :
            !!i.checked;
          const visible = (checklist.items || [])
            .filter(i => categoryFilter === "all" || (i.category || "documents") === categoryFilter)
            .filter(i => matchStatus(i) || stuckIds.has(i.id));

          if (visible.length === 0) {
            const emptyMsg =
              statusFilter === "todo" ? t.modes.organizzativa.statusEmptyTodo :
              statusFilter === "done" ? t.modes.organizzativa.statusEmptyDone :
              null;
            if (emptyMsg) {
              return (
                <div
                  data-testid="checklist-status-empty"
                  className="rounded-2xl border border-dashed border-border bg-surface-card p-6 text-center text-ink-soft text-sm font-sans"
                >
                  {emptyMsg}
                </div>
              );
            }
          }

          return (
            <ChecklistSection
              t={t}
              title={t.modes.organizzativa.unifiedTitle}
              testidPrefix="checklist"
              templateId={checklist.template_id || "tmpl_predep_us"}
              items={visible.map(i => ({ ...i, _section: "unified", _templateId: checklist.template_id }))}
              urgencyFor={urgencyFor}
              onToggleItem={toggleItem}
              onSetDeadline={setDeadline}
              onRefresh={async () => { await loadChecklist(); refreshPhaseState(); }}
              onEditProfile={openProfilePanel}
              isAnonymous={isAnonymous}
              onAnonymousMutation={forceAuthPrompt}
            />
          );
        })()}
      </section>

      {/* Smaller "personalize profile" affordance — moved here from above
          the checklist. Rendering state mirrors the original three-way split
          (summary chip when filled, dashed CTA when empty) but with reduced
          visual weight so first-time visitors land directly on the tasks. */}
      {!editingProfile && (profileHasContent ? (
        <div data-testid="profile-summary" className="rounded-2xl border border-border bg-surface-card p-4 flex items-start justify-between gap-3 flex-wrap">
          <div className="text-xs sm:text-sm font-sans text-ink-soft leading-relaxed">
            {profile.home_school && (
              <div className="text-ink font-semibold">{profile.home_school}</div>
            )}
            {profileSummaryParts.length > 0 && (
              <div>{profileSummaryParts.join(" · ")}</div>
            )}
          </div>
          <Button
            data-testid="profile-edit"
            variant="ghost"
            size="sm"
            onClick={openProfilePanel}
            className="rounded-full text-terracotta hover:bg-terracotta/5"
          >
            <PencilSimple size={14} weight="duotone" className="mr-1" />
            {t.modes.organizzativa.profileEdit}
          </Button>
        </div>
      ) : (
        <div data-testid="personalize-profile-cta" className="rounded-2xl border border-dashed border-terracotta/40 bg-terracotta/5 p-4 flex items-start justify-between gap-3 flex-wrap">
          <div className="text-xs sm:text-sm font-sans text-ink-soft leading-relaxed max-w-md">
            <div className="text-ink font-semibold font-sans text-sm">
              {t.modes.organizzativa.personalizeProfileCta}
            </div>
            <div className="mt-1 text-xs">
              {t.modes.organizzativa.personalizeProfileHint}
            </div>
          </div>
          <Button
            data-testid="personalize-profile-open"
            size="sm"
            variant="outline"
            onClick={openProfilePanel}
            className="rounded-full border-terracotta/50 text-terracotta hover:bg-terracotta/10 font-sans font-semibold"
          >
            <PencilSimple size={14} weight="duotone" className="mr-1" />
            {t.modes.organizzativa.profileEdit}
          </Button>
        </div>
      ))}

      {/* "Salva i tuoi progressi" — anonymous-only prompt rendered after the
          list. Fires the "Organizza Signup Clicked" Plausible event with a
          callback so the beacon flushes before navigation.  */}
      {isAnonymous && (
        <section
          data-testid="organizza-save-prompt"
          className="rounded-3xl border border-terracotta/30 bg-terracotta/5 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between"
        >
          <div className="min-w-0">
            <h3 className="font-display font-extrabold text-lg sm:text-xl text-ink tracking-tight">
              {t.modes.organizzativa.savePromptTitle}
            </h3>
            <p className="text-sm text-ink-soft font-sans leading-relaxed mt-1 max-w-xl">
              {t.modes.organizzativa.savePromptBody}
            </p>
          </div>
          <Button
            data-testid="organizza-save-prompt-cta"
            onClick={() => {
              trackEvent(
                "Organizza Signup Clicked",
                {},
                () => navigate("/login", { state: { after: "/organizza" } }),
              );
            }}
            className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold shrink-0 w-full sm:w-auto"
          >
            {t.modes.organizzativa.savePromptCta}
          </Button>
        </section>
      )}

      <AuthPromptModal
        open={authPromptOpen}
        onOpenChange={setAuthPromptOpen}
        reason={authPromptReason}
        afterPath="/organizza"
      />

      {/* Plan 100% completion modal — non-blocking. Appears once when the
          checklist crosses to 100%. Both actions call the backend ack endpoint
          so Check-in stays unlocked from now on. */}
      <Dialog open={showCompletionModal} onOpenChange={(open) => {
        if (!open) {
          setShowCompletionModal(false);
          acknowledgePlanCompletion();
        }
      }}>
        <DialogContent data-testid="plan-completed-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold text-xl text-ink">
              {t.planCompleted.title}
            </DialogTitle>
            <DialogDescription className="text-ink-soft leading-relaxed">
              {t.planCompleted.body}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              data-testid="plan-completed-stay"
              variant="outline"
              onClick={async () => {
                setShowCompletionModal(false);
                await acknowledgePlanCompletion();
              }}
              className="rounded-full border-terracotta text-terracotta hover:bg-terracotta/5 font-sans font-semibold"
            >
              {t.planCompleted.stayHere}
            </Button>
            <Button
              data-testid="plan-completed-go-checkin"
              onClick={async () => {
                setShowCompletionModal(false);
                await acknowledgePlanCompletion();
                navigate("/checkin");
              }}
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold"
            >
              {t.planCompleted.goCheckin}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
